import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { NAV, SITE } from "@/config/site";
import { Logo } from "./Logo";
import { DonateButton, Pending, Val, telHref } from "./Primitives";
import { FloridaDisclosure } from "./FloridaDisclosure";

const FooterCol = ({ title, children }) => (
  <div>
    <h2 className="font-display text-xs font-bold uppercase tracking-[0.24em] text-gold">{title}</h2>
    <div className="mt-5 space-y-3 text-sm text-[#C9D4DF]">{children}</div>
  </div>
);

export const Footer = () => {
  const { contact, social, nonprofit } = SITE;
  return (
    <footer className="relative overflow-hidden bg-navy-deep text-paper" data-testid="site-footer">
      <div className={`container-x grid gap-12 py-20 md:grid-cols-2 ${social.some((s) => s.url) ? "lg:grid-cols-[1.4fr_1fr_1fr_1fr]" : "lg:grid-cols-[1.6fr_1fr_1.2fr]"}`}>
        <div>
          <Logo size="h-28 w-auto" />
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-[#C9D4DF]">
            Supporting firefighters, law enforcement officers, emergency medical personnel, dispatchers, and veterans across South Florida through programs that build connection, resilience, and community.
          </p>
          <DonateButton label="Donate Now" className="mt-8" testId="footer-donate-button" />
        </div>
        <FooterCol title="Explore">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="link-underline block w-fit hover:text-paper" data-testid={`footer-link-${n.to.replace(/\//g, "") || "home"}`}>
              {n.label}
            </Link>
          ))}
        </FooterCol>
        <FooterCol title="Contact">
          <p className="flex items-start gap-3"><Mail size={16} className="mt-0.5 shrink-0 text-gold" /><Val value={contact.email} href={`mailto:${contact.email}`} label="Foundation email" dark /></p>
          <p className="flex items-start gap-3"><Phone size={16} className="mt-0.5 shrink-0 text-gold" /><Val value={contact.phone} href={telHref(contact.phone)} label="Foundation phone" dark /></p>
          <p className="flex items-start gap-3"><MapPin size={16} className="mt-0.5 shrink-0 text-gold" /><Val value={contact.mailingAddress} label="Mailing address" dark /></p>
          <Link to="/about#contact" className="link-underline block w-fit text-paper" data-testid="footer-contact-form-link">Send us a message</Link>
        </FooterCol>
        {social.some((s) => s.url) && (
          <FooterCol title="Follow">
            {social.filter((s) => s.url).map((s) => (
              <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" className="link-underline block w-fit hover:text-paper" data-testid={`footer-social-${s.label.toLowerCase()}`}>{s.label}</a>
            ))}
          </FooterCol>
        )}
      </div>
      <div className="hairline" />
      <div className="container-x flex flex-col gap-6 py-8 text-xs leading-relaxed text-[#AFBFCE] lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl" data-testid="footer-nonprofit-disclosure">
          <p>{nonprofit.status || nonprofit.ein
            ? `${SITE.name} is ${nonprofit.status}${nonprofit.ein ? `, EIN ${nonprofit.ein}` : ""}. ${nonprofit.taxDeductibility}`
            : "Nonprofit status, EIN, tax-deductibility, and any applicable state registration disclosures will be published here once verified by the Foundation."}{" "}
          {!nonprofit.status && <Pending label="nonprofit disclosures" dark />}</p>
          <FloridaDisclosure dark className="mt-3" testId="footer-florida-disclosure" />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Link to="/privacy-policy" className="link-underline hover:text-paper" data-testid="footer-privacy-link">Privacy Policy</Link>
          <Link to="/accessibility" className="link-underline hover:text-paper" data-testid="footer-accessibility-link">Accessibility</Link>
          <span>© {new Date().getFullYear()} {SITE.name}</span>
        </div>
      </div>
    </footer>
  );
};
